import { useWatch } from 'react-hook-form';

import { useSankeyFormContext } from 'contexts/sankeyFormContext';

import { reportingPeriodOptions, statementTypeOptions } from '../constants';
import {
  CanvasSizeEnum,
  ReportingPeriodEnum,
  StatementTypeEnum,
} from '../generated-api';

// Returns the human readable label for a given ReportingPeriodEnum value
export const getReportingPeriodLabel = (value: ReportingPeriodEnum) =>
  reportingPeriodOptions.find((o) => o.value === value)?.label ?? 'Full Year';

// Returns the human readable label for a given StatementTypeEnum value
export const getStatementTypeLabel = (value: StatementTypeEnum) =>
  statementTypeOptions.find((o) => o.value === value)?.label ?? '';

// If canvas_size is small, medium or large (1, 2 or 3 respectively),
// return the hardcoded width and height, otherwise return the width
// and height from the custom number inputs.
export const getCanvasDimensions = ({
  canvas_size,
  canvas_width,
  canvas_height,
}: Pick<
  SankeyFormValues,
  'canvas_size' | 'canvas_width' | 'canvas_height'
>) => {
  switch (canvas_size) {
    case CanvasSizeEnum._1:
      return { width: 352, height: 300 };
    case CanvasSizeEnum._2:
      return { width: 800, height: 480 };
    case CanvasSizeEnum._3:
      return { width: 960, height: 600 };
    default:
      return { width: canvas_width ?? 960, height: canvas_height ?? 600 };
  }
};

// Hook to get the canvas dimensions from the form context
export const useCanvasDimensions = () => {
  const { form } = useSankeyFormContext();
  const [canvas_size, canvas_width, canvas_height] = useWatch({
    control: form.control,
    name: ['canvas_size', 'canvas_width', 'canvas_height'],
  });
  return getCanvasDimensions({ canvas_size, canvas_width, canvas_height });
};
